import { html } from '@acme/b2b/utils';
import { GridDefinitionFactory } from '@b2b/common/factories/gridDefinition/types';
import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { Impersonation, UserSession } from '@b2b/common/services/auth/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { PermissionsService } from '@b2b/common/services/permissions';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ACMEResponse, ColumnDef, Timestamp, TODO, UIBModalService } from '@b2b/types/core';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IScope, ITimeoutService } from 'angular';

OrdersListController.$inject = [
  '$scope',
  '$state',
  '$uibModal',
  '$timeout',
  '$filter',
  'ApiService',
  'CalendarService',
  'PermissionsService',
  'ViewStateService',
  'AuthenticationService',
  'GridStateFactory',
  'GridDefinitionFactory',
  'UserPreferencesService',
];

function OrdersListController(
  $scope: IScope & { ordersList: TODO },
  $state: StateService,
  $uibModal: UIBModalService,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  ApiService: ApiService,
  CalendarService: CalendarService,
  PermissionsService: PermissionsService,
  ViewStateService: TODO,
  authenticationService: AuthenticationService,
  GridStateFactory: GridStateFactory,
  GridDefinitionFactory: GridDefinitionFactory,
  UserPreferencesService: UserPreferencesService,
) {
  const ctrl = {
    model: [],
    filtered: [],
    typed: [],
    searchColumn: {},
    lastSearchQuery: '',
    searchQuery: ViewStateService.getStateProperty('orders.list', 'search', ''),
    queryTimer: false as IPromise<void> | false,
    searchDate: true,
    orderCategory: {} as TODO,
    showListType: {},
    startDate: '' as string | Date | null,
    endDate: '',
    startTime: '',
    endTime: '',
    gridState: new GridStateFactory(),
    gridDefinition: new GridDefinitionFactory(),
    searchEventType: {},
    selectedOrderId: '',
    dateFormat: UserPreferencesService.getDateFormat('fieldDate', 'dateFormat'),
    dateTimeFormat: UserPreferencesService.getDateTimeFormat('sortDateTime'),
    isLoaded: false,
    searchColumns: [
      { name: 'orderNumber', label: 'Order Number' },
      { name: 'email', label: 'Email' },
      { name: 'contactName', label: 'Name' },
    ],
    categories: [
      { label: 'Any order type', name: 'all' },
      { label: 'Individual', name: 'individual' },
      { label: 'Organization', name: 'account' },
    ],
    viewId: '',
    ordersListGridName: $state.current.name + '.grid0',

    optionsOverride: {
      useAccessibleGrid: true,
      rowHeight: 40,
      data: 'ordersList.model',
      appScopeProvider: {
        onDataGridPaginate: (newPageNumber, newPageSize, sortField, sortDirection) => {
          ctrl.onDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDataGridSortChange: (grid, columns, page) => {
          ctrl.onDataGridSortChange(grid, columns, page);
        },
        onDblClick: (item) => {
          ctrl.onDblClick(item);
        },
        dateFormat: (date) => {
          return CalendarService.toFormat(date, ctrl.dateTimeFormat);
        },
      },
      gridMenuCustomItems: [
        {
          title: 'Export results to CSV',
          order: 100,
          action: ($event) => {
            ctrl.invoke(true);
          },
        },
      ],
      columnDefs: [
        {
          field: 'view',
          displayName: 'View',
          width: 50,
          paginationPageSizes: [10],
          onCellNavClick: (row, col) => {
            ctrl.optionsOverride.appScopeProvider.onDblClick(row);
          },
          cellTemplate: html`
            <div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()">
              <a data-ng-click="grid.appScope.onDblClick(row)" role="link">
                View<span class="sr-only"> Details for {{ row.entity.orderNumber }}</span>
              </a>
            </div>
          `,
          template: html`<div class="clickable ui-grid-cell-contents" ng-class="col.colIndex()"><a>View</a></div>`,
          visible: true,
          enableHiding: false,
          enableSorting: false,
        },
        {
          field: 'tenantId',
          displayName: 'Tenant Id',
          visible: false,
        },
        {
          field: 'tenantName',
          displayName: 'Tenant Name',
          visible: false,
        },
        {
          field: 'accountName',
          displayName: 'Organization',
          visible: false,
        },
        {
          field: 'orderNumber',
          displayName: 'Order Number',
        },
        {
          field: 'contactLastName',
          displayName: 'Last Name',
        },
        {
          field: 'contactFirstName',
          displayName: 'First Name',
        },
        {
          field: 'billingLastName',
          displayName: 'Billing Last Name',
          visible: false,
        },
        {
          field: 'billingFirstName',
          displayName: 'Billing First Name',
          visible: false,
        },
        {
          field: 'totalAmount',
          displayName: 'Price',
          cellTemplate: html`<div class="ui-grid-cell-contents">{{ (COL_FIELD || 0) }}</div>`,
          cellRenderer: (params) => {
            return params.data.totalAmount || 0;
          },
          cellFilter: 'currency',
          cellFormatter: (item) => {
            return $filter('currency')(item.value);
          },
          visible: false,
        },
        {
          field: 'discountedTotalAmount',
          displayName: 'Discounted Price',
          cellTemplate: html`<div class="ui-grid-cell-contents">{{ (COL_FIELD || 0) }}</div>`,
          cellRenderer: (params) => {
            return params.data.discountedTotalAmount || 0;
          },
          cellFilter: 'currency',
          cellFormatter: (item) => {
            return $filter('currency')(item.value);
          },
          visible: false,
        },
        {
          field: 'balanceAmount',
          displayName: 'Balance Due',
          cellTemplate: html`<div class="ui-grid-cell-contents">{{ (COL_FIELD || 0) }}</div>`,
          cellRenderer: (params) => {
            return params.data.balanceAmount || 0;
          },
          cellFilter: 'currency',
          cellFormatter: (item) => {
            return $filter('currency')(item.value);
          },
          visible: false,
        },
        {
          field: 'saleChannel',
          displayName: 'Channel',
          visible: true,
        },
        {
          field: 'email',
          displayName: 'Email',
          visible: true,
        },
        {
          field: 'billingEmail',
          displayName: 'Billing Email',
          visible: false,
        },
        {
          field: 'ccLastFourDigits',
          displayName: 'CC Last 4',
          visible: true,
        },
        {
          field: 'paymentId',
          displayName: 'Voucher/check',
          visible: true,
        },
        {
          field: 'zipCode',
          displayName: 'Zip Code',
          visible: true,
        },
        {
          field: 'createdOn',
          displayName: 'Order Date',
          cellTemplate: html`
            <div class="ui-grid-cell-contents">{{ grid.appScope.dateFormat(row.entity.createdOn) }}</div>
          `,
          sort: {
            direction: 'asc',
          },
        },
      ] as ColumnDef[],
    },

    init: function () {
      ctrl.viewId = $state.current.name ?? '';

      ctrl.gridDefinition.init(
        ctrl.ordersListGridName,
        ctrl.optionsOverride,
        ctrl.viewId,
        $state.current.data.enablePreferences,
      );

      $scope.$emit('Application::contentTitleChange', {
        title: 'Orders',
      });

      ctrl.restoreViewState();

      ctrl.addHandlers();
      $scope.$on('DataGrid::UserPreferencesLoaded', ctrl.initData);

      $scope.$on('destroy', () => {
        if (ctrl.queryTimer) {
          $timeout.cancel(ctrl.queryTimer);
        }
      });
    },

    restoreViewState: function () {
      const viewState = ViewStateService.getState('orders.list');
      ctrl.startDate = viewState.date ? CalendarService.parseDate(viewState.date) : '';
      ctrl.orderCategory = viewState.type ? ctrl.findOrderCategory(viewState.type)?.category : ctrl.categories[0];

      if (viewState.page) {
        ctrl.gridState.setPropertyValue('page', viewState.page);
        ctrl.gridDefinition.setPageNumber(viewState.page);
      }
    },

    addHandlers: function () {
      $scope.$watch('ordersList.startDate', ctrl.onStartDateUpdate);
      $scope.$watch('ordersList.searchDate', ctrl.onSearchDateUpdate);

      $scope.$on('pageContent::startLoad', (event, data) => {
        let startDate;

        if (data && data.from && data.from.name === 'application.orders.list') {
          switch (data.to.name) {
            case 'application.orders.detail':
            case 'application.orders.detail.summary':
            case 'application.orders.detail.emailStatus':
              break;

            default:
              startDate = ctrl.getToday();
              CalendarService.current.eventsStartTimeTracker = null;
              ctrl.startDate = startDate;

              ViewStateService.resetState('orders.list');
          }
        }
      });
    },

    initData: function () {
      const props = ctrl.gridDefinition.getGridState();

      ctrl.dateFormat = UserPreferencesService.getDateFormat('fieldDate', 'dateFormat');
      ctrl.dateTimeFormat = UserPreferencesService.getDateTimeFormat('shortDateTime');

      ctrl.gridState.updateGridState(props.page, props.pageSize, props.sortField, props.sortDirection);

      ctrl.invoke();
    },

    findOrderCategory: function (name: string) {
      for (let i = 0, ii = ctrl.categories.length; i < ii; i++) {
        if (ctrl.categories[i].name === name) {
          return {
            index: i,
            category: ctrl.categories[i],
          };
        }
      }

      return undefined;
    },

    query: function () {
      if (ctrl.queryTimer) {
        $timeout.cancel(ctrl.queryTimer);
      }

      ctrl.queryTimer = $timeout(() => {
        $scope.$broadcast('DataGrid::Search', {
          name: ctrl.ordersListGridName,
        });
      }, 500);
    },

    invoke: function (downloadCsv = false) {
      let query: Partial<{ search: string; startDate: string; endDate: string; orderCategory: TODO }> = {};

      ctrl.isLoaded = false;

      if (ctrl.searchQuery || ctrl.searchQuery === '') {
        query.search = ctrl.searchQuery;
        ctrl.gridState.setPropertyValue('search', ctrl.searchQuery);
        ctrl.lastSearchQuery = ctrl.searchQuery;
      }

      if (ctrl.searchDate) {
        if (ctrl.startTime) {
          query.startDate = ctrl.startTime;
        }
        if (ctrl.endTime) {
          query.endDate = ctrl.endTime;
        }
      } else {
        delete query.startDate;
        delete query.endDate;
      }

      // TODO: APP-4432 - should we remove the tenantName column?
      ctrl.gridDefinition.setColumnVisibility({ tenantName: false });

      switch (ctrl.orderCategory.name) {
        case 'account':
          query.orderCategory = ctrl.orderCategory.name;
          ctrl.gridDefinition.setColumnVisibility({
            accountName: true,
            totalAmount: true,
            discountedTotalAmount: false,
            balanceAmount: false,
            ccLastFourDigits: false,
            paymentId: false,
            zipCode: false,
          });
          break;
        case 'individual':
          query.orderCategory = ctrl.orderCategory.name;
          ctrl.gridDefinition.setColumnVisibility({
            accountName: false,
            totalAmount: false,
            discountedTotalAmount: true,
            balanceAmount: true,
            ccLastFourDigits: true,
            paymentId: true,
            zipCode: true,
          });
          break;
        default:
          ctrl.gridDefinition.setColumnVisibility({
            accountName: true,
            totalAmount: true,
            discountedTotalAmount: true,
            balanceAmount: true,
            ccLastFourDigits: true,
            paymentId: true,
            zipCode: true,
          });
      }

      query = $.extend(query, ctrl.gridState.getGridState());

      if (ctrl.searchDate) {
        if (downloadCsv) {
          ctrl.getCsv(query);
        } else {
          ApiService.orders
            .search(query)
            .then((response) => ctrl.success.search(response))
            .catch((response) => ctrl.error.search(response));
        }
      } else {
        if (downloadCsv) {
          ctrl.getCsv(query);
        } else {
          ApiService.orders
            .list(query)
            .then((response) => ctrl.success.search(response))
            .catch((response) => ctrl.error.search(response));
        }
      }
    },

    hasOrders: function () {
      return Array.isArray(ctrl.model) && ctrl.model.length > 0;
    },

    updateState: function () {
      const state: { page: number; date: Timestamp | null; search: string } = {
        page: ctrl.gridState.getPropertyValue('page'),
        date: ctrl.startDate == null ? ctrl.startDate : CalendarService.toISO8601(ctrl.startDate),
        search: ctrl.searchQuery,
      };

      ViewStateService.setState('orders.list', state);
    },

    process: function (orders) {
      for (let i = 0, ii = orders.length; i < ii; i++) {
        orders[i].createdOn = CalendarService.parseDate(orders[i].createdOn);
      }

      return orders;
    },

    search: function () {
      $scope.$broadcast('DataGrid::Search', { name: ctrl.ordersListGridName });
    },

    getCsv: function (
      query: Partial<{
        search: string;
        startDate: string;
        endDate: string;
        orderCategory: TODO;
      }>,
    ) {
      ApiService.orders
        .csv(angular.extend({ useForm: true }, query))
        .then((response) => ctrl.success.getCsv(response))
        .catch((response) => ctrl.error.getCsv(response));
    },

    getToday: function () {
      const date = new Date();
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      return date;
    },

    clearStartDate: function () {
      ctrl.startDate = null;
    },

    onStartDateUpdate: function (n: TODO, o: TODO) {
      const startDate = ctrl.startDate;

      if (startDate instanceof Date) {
        startDate.setHours(0);
        startDate.setMinutes(0);
        startDate.setSeconds(0);

        ctrl.startTime = CalendarService.toISO8601(startDate);
        ctrl.searchDate = true;

        const endDate = new Date(startDate);
        endDate.setHours(23);
        endDate.setMinutes(59);
        endDate.setSeconds(59);
        ctrl.endTime = CalendarService.toISO8601(endDate);
      } else {
        ctrl.searchDate = false;
      }

      if (n !== o) {
        $scope.$broadcast('DataGrid::Search', { name: ctrl.ordersListGridName });
      }
    },

    CSRSignIn: function (id: number, name: string) {
      $uibModal
        .open({
          templateUrl: 'views/application/views/sherpa/customers/modals/signIn.tpl.html',
          controller: 'SherpaCustomersSignInModalController',
          resolve: {
            currentTenant: () => {
              return {
                id: id,
                name: name,
              };
            },
          },
        })
        .result.then(() => {
          authenticationService
            .deputySignIn(id)
            .then((response) => ctrl.success.CSRSignIn(response))
            .catch((response) => ctrl.error.CSRSignIn(response));
        })
        .catch(() => {
          // noop
        });
    },

    showMessage: function (title: string, message: string) {
      if (message) {
        $uibModal
          .open({
            templateUrl: 'views/application/views/reports/create/modals/showMessage.tpl.html',
            size: 'md',
            controller: 'ReportsCreateShowMessageModalController',
            resolve: {
              currentTitle: () => title,
              currentMessage: () => message,
              safe: () => true,
            },
          })
          .result.then(() => {
            // noop
          })
          .catch(() => {
            // noop
          });
      }
    },

    completeCSRSignIn: function (id: number) {
      ApiService.user.permissions
        .list(id)
        .then((response) => ctrl.success.completeCSRSignIn(response))
        .catch((response) => ctrl.error.completeCSRSignIn(response));
    },

    success: {
      search: function (response) {
        ctrl.model = ctrl.process(response.data.payload.list);
        ctrl.isLoaded = true;
        ctrl.gridDefinition.isLoaded = true;
        $timeout(() => {
          $scope.$broadcast('DataGrid::ResizeGrid', {
            name: ctrl.gridDefinition.options.gridName,
            datasetLength: ctrl.model.length,
            totalItems: response.data.payload.pagination.count,
          });
        }, 10);
      },

      getCsv: function (response) {
        ctrl.isLoaded = true;
        ctrl.gridDefinition.isLoaded = true;
      },

      CSRSignIn: function (response: {
        data: ACMEResponse<{
          user: UserSession;
          impersonation: Impersonation | null;
        }>;
      }) {
        ctrl.completeCSRSignIn(response.data.payload.user.id);
      },

      completeCSRSignIn: function (response) {
        PermissionsService.clearChecksum();
        PermissionsService.store(response.data.payload);
        // Next tick
        $timeout(() => {
          $state.go('application.orders.detail.summary', { orderId: ctrl.selectedOrderId }, { reload: true });
          PermissionsService.refresh($scope);
        }, 0);
      },
    },

    error: {
      init: function (response) {},

      generic: function (response) {
        ctrl.showMessage('An error has occurred', response.data.error);
      },

      search: function (response) {
        ctrl.error.generic(response);
        ctrl.isLoaded = true;
        ctrl.gridDefinition.isLoaded = true;
      },

      getCsv: function (response) {
        ctrl.showMessage('An error has occurred', response.data.error || 'Unable to download CSV');
        ctrl.isLoaded = true;
        ctrl.gridDefinition.isLoaded = true;
      },

      CSRSignIn: function (response) {
        ctrl.error.generic(response);
      },

      completeCSRSignIn: function (response) {
        ctrl.error.generic(response);
      },
    },

    onSearchDateUpdate: function (n, o) {
      if (n !== o) {
        $scope.$broadcast('DataGrid::ResetCurrentPage');
      }
    },

    onSearchKeyUp: function (e) {
      if (ctrl.searchQuery !== ctrl.lastSearchQuery) {
        $scope.$broadcast('DataGrid::ResetCurrentPage');
        $scope.$broadcast('DataGrid::Search', { name: ctrl.ordersListGridName });
      }
    },

    onDataGridPaginate: function (newPageNumber, newPageSize, sortField, sortDirection) {
      ctrl.gridState.updateGridState(newPageNumber, newPageSize);
      if (sortField) {
        ctrl.gridState.updateGridState(null, null, sortField, null);
      }
      if (sortDirection) {
        ctrl.gridState.updateGridState(null, null, null, sortDirection);
      }
      ctrl.invoke();
    },

    onDataGridSortChange: function (grid, columns, page) {
      const data = columns[0];

      ctrl.gridState.updateGridState(page || null, null, data.field, data.sort.direction);
      ctrl.invoke();
    },

    onDblClick: function (item) {
      const user = authenticationService.getUserFromCookie();

      ctrl.selectedOrderId = item.entity.orderId || item.entity.id;

      if (parseInt(item.entity.tenantId, 10) === user?.tenantId) {
        ctrl.updateState();
        $state.go('application.orders.detail.summary', { orderId: ctrl.selectedOrderId });
      } else if (PermissionsService.check('CSR')) {
        /**
         * It's very likely that this if statement is no longer needed because we can't search by all tenants anymore
         * // TODO: verify and remove this if
         * @link https://acmeticketing.atlassian.net/browse/APP-4432
         */

        ctrl.updateState();
        ctrl.CSRSignIn(item.entity.tenantId, item.entity.tenantName);
      } else {
        ctrl.showMessage('Permission Denied', 'You do not have sufficient permissions to view this order.');
      }
    },
  };

  $scope.ordersList = ctrl;
  ctrl.init();
}

OrdersListDatePickerController.$inject = ['$scope', 'UserPreferencesService', '$timeout'];

function OrdersListDatePickerController(
  $scope: IScope & { ordersListDatePicker: TODO; dt: Date | null; minDate: Date | null },
  UserPreferencesService: TODO,
  $timeout: ITimeoutService,
) {
  const ctrl = {
    dt: null as Date | null,
    isOpen: false,
    isPreferenceLoaded: false,
    dateOptions: {
      formatYear: 'yy',
      startingDay: UserPreferencesService.getStartDayOfWeek(),
      formatDayTitle: UserPreferencesService.getDateFormat('month', 'dateFormat'),
      showWeeks: false,
    } as TODO,

    init: function () {
      $scope.$on('UserPreferencesService:onDateFormatLoad', (e, data) => {
        ctrl.dateOptions = {
          formatYear: 'yy',
          startingDay: UserPreferencesService.getStartDayOfWeek(),
          formatDayTitle: UserPreferencesService.getDateFormat('month', 'dateFormat'),
        };
      });

      $timeout(() => {
        ctrl.isPreferenceLoaded = true;
      }, 0);
    },

    today: function () {
      ctrl.dt = new Date();
    },
    clear: function () {
      $scope.dt = null;
    },

    toggleMin: function () {
      $scope.minDate = $scope.minDate ? null : new Date();
    },
    open: function ($event) {
      $event.preventDefault();
      $event.stopPropagation();
      ctrl.isOpen = true;
    },
  };

  $scope.ordersListDatePicker = ctrl;
  ctrl.init();
}

/**
 * For maintainability we should separate controllers into their own modules
 *
 * // TODO: display current date in calendar, and state date also
 * // TODO: place calendar in upper nav showing only when necessary
 */
export const OrdersListControllerModule = angular
  .module('acme.view.application.orders.list.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    'acme.common.service.viewState',
    'acme.common.factory.gridState',
    'acme.common.factory.gridDefinition',
    'acme.view.application.sherpa.customers.signInModal.controllers',
    'acme.view.application.reports.create.showMessageModal.controllers',
    'acme.common.service.userPreferences',
    AuthenticationServiceModule,
  ])

  .controller('OrdersListController', OrdersListController)
  .controller('OrdersListDatePickerController', OrdersListDatePickerController).name;
